/**
 * Coach Engine — Trade Scope
 *
 * Per-trade observation orchestrator. Takes a single trade and runs
 * registered trade-scope analyzers. Used by the trade-detail modal so
 * the trader gets an opinion on THIS trade specifically — sharper than
 * the day/setup aggregate observations and closer to "what should I
 * learn from this one."
 *
 * Different from other scopes:
 *   - day:      one date, all trades
 *   - progress: cross-session/cross-day trends
 *   - setup:    one setup, all of its history
 *   - atm:      one ATM, all of its history
 *   - session:  one session group of trades
 *   - trade:    ONE trade ← this scope
 *
 * Adding a new trade-scope analyzer:
 *   1. Create the module under ../analyzers/trade/
 *   2. Push into ANALYZERS below
 *   3. Done — ranking handled here, scope dispatch wires up in index.js.
 */

import { topObservations } from '../priorities';
import * as tradeNarrative from '../analyzers/trade/tradeNarrative';
// Top-Down trade-scope analyzer (Phase C.2, v7.5.15) — emits 5 observation
// types from one MultiTfState synthesis: aligned / counter-bias / zone-
// approach / alwaysin-state / stage-context. See
// docs/research/Coach_TopDown_Analysis.md §4.
import * as topDownContext from '../analyzers/composite/topDown/topDownContext';

const ANALYZERS = [
  tradeNarrative,
  topDownContext,
];

const TIER_LEVEL = { free: 0, pro: 1 };

const analyzersForTier = (userTier = 'free') => {
  const level = TIER_LEVEL[userTier] ?? 0;
  return ANALYZERS.filter(a => (TIER_LEVEL[a.tier || 'free'] ?? 0) <= level);
};

/**
 * Run the registered trade-scope analyzers and return ranked observations.
 *
 * @param {Object} ctx
 * @param {Object} ctx.trade        The trade object (must have barAnalysis to be useful)
 * @param {Array}  [ctx.allTrades]  Full trade history — analyzers can use for setup/ATM context
 * @param {string} [ctx.tier]       'free' | 'pro' — tier-gates analyzers
 * @param {number} [ctx.top]        Cap output count (default unlimited)
 * @returns {Array<Observation>}
 */
export const analyzeTrade = (ctx = {}) => {
  const { trade, tier = 'free', top } = ctx;
  if (!trade || typeof trade !== 'object') return [];
  if (!trade.id) return [];

  const analyzers = analyzersForTier(tier);
  const all = [];
  for (const analyzer of analyzers) {
    try {
      const result = analyzer.analyze({ ...ctx, trade, tier });
      if (Array.isArray(result)) all.push(...result);
      else if (result) all.push(result);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[Coach trade] ${analyzer.key || 'unknown'} failed:`, e?.message || e);
    }
  }

  if (all.length === 0) return [];
  return topObservations(all, top);
};
